import { DEFAULT_TIMEOUT_MS, isTimeoutError } from './_transport';
import type { ExchangeRefusalCode, ExchangeUnavailableCode, SessionOutcome } from './types';

const TERMINAL_EXCHANGE_CODES = new Set<string>([
  'unauthorized',
  'account_access_denied',
  'account_not_provisioned',
  'workspace_unavailable',
]);

const UNAVAILABLE_EXCHANGE_CODES = new Set<string>(['identity_provider_unavailable', 'token_mint_unavailable']);

export class AmsExchangeClient {
  private readonly exchangeUrl: string;

  private readonly timeoutMs: number;

  constructor(config: { exchangeUrl: string; timeoutMs?: number }) {
    this.exchangeUrl = config.exchangeUrl;
    this.timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  }

  async exchange(params: { idToken: string }): Promise<SessionOutcome> {
    let response: Response;
    try {
      response = await fetch(this.exchangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: params.idToken }),
        signal: AbortSignal.timeout(this.timeoutMs),
      });
    } catch (error) {
      return { kind: 'unavailable', source: 'transport', code: isTimeoutError(error) ? 'timeout' : 'network' };
    }

    if (response.ok) {
      let data: Record<string, unknown>;
      try {
        data = (await response.json()) as Record<string, unknown>;
      } catch (error) {
        console.error('AMS exchange endpoint returned a non-JSON success response', error);
        return { kind: 'unavailable', source: 'transport', code: 'network' };
      }
      const accessToken = data.access_token;
      const expiresIn = data.expires_in;
      if (typeof accessToken !== 'string' || typeof expiresIn !== 'number' || !Number.isFinite(expiresIn) || expiresIn <= 0) {
        console.error('AMS exchange endpoint returned a 200 response with an invalid token payload');
        return { kind: 'unavailable', source: 'transport', code: 'network' };
      }
      return { kind: 'minted', accessToken, expiresIn };
    }

    const code = await this.readErrorCode(response);
    if (code && TERMINAL_EXCHANGE_CODES.has(code)) {
      return { kind: 'terminal', source: 'exchange', code: code as ExchangeRefusalCode };
    }
    if (code && UNAVAILABLE_EXCHANGE_CODES.has(code)) {
      return { kind: 'unavailable', source: 'exchange', code: code as ExchangeUnavailableCode };
    }
    console.error('AMS exchange endpoint returned an unrecognized error code', code, response.status);
    return { kind: 'unavailable', source: 'transport', code: 'network' };
  }

  private async readErrorCode(response: Response): Promise<string | null> {
    try {
      const body = (await response.json()) as { code?: unknown };
      return typeof body.code === 'string' ? body.code : null;
    } catch {
      return null;
    }
  }
}
